import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { TOOLBAR_CONTROL } from '@/components/ui/pageChrome';
import { TYPE, UI_MUTED, UI_SECTION } from '@/lib/typography';
import { usePatientPicker } from '@/hooks/usePatientPicker';
import { countActiveMoreFilters } from '@/lib/reporting/filters';
import type { ReportFilterState, ReportGrouping } from '@/lib/reporting/types';
import { cn } from '@/utils/cn';
import { MultiSelectFilter, type MultiSelectOption } from './MultiSelectFilter';

const INVOICE_STATUSES = [
  'issued',
  'partial',
  'paid',
  'overdue',
  'refunded',
  'cancelled',
  'void',
] as const;

interface MoreFiltersPopoverProps {
  clinicId: string | null;
  filters: ReportFilterState;
  defaultGrouping: ReportGrouping;
  paymentMethods: string[];
  locationOptions: MultiSelectOption[];
  patientLabels: Record<string, string>;
  onChange: (filters: ReportFilterState) => void;
  onClear: () => void;
}

export function MoreFiltersPopover({
  clinicId,
  filters,
  defaultGrouping,
  paymentMethods,
  locationOptions,
  patientLabels,
  onChange,
  onClear,
}: MoreFiltersPopoverProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [patientQuery, setPatientQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const picker = usePatientPicker(clinicId, patientQuery);
  const activeCount = countActiveMoreFilters(filters, { grouping: defaultGrouping });

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  const fieldClass = cn(
    TOOLBAR_CONTROL,
    'w-full font-normal text-ds-text focus:border-ds-teal focus:outline-none focus:shadow-ring',
  );

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={cn(TOOLBAR_CONTROL, 'inline-flex items-center gap-2')}
      >
        <Filter className="h-3.5 w-3.5" />
        {t('reports.more_filters')}
        {activeCount > 0 && (
          <span className="inline-flex min-w-[1.15rem] items-center justify-center rounded-full bg-ds-teal-tint px-1.5 text-[11px] font-semibold text-ds-teal">
            {activeCount}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute left-0 z-30 mt-s1 max-h-[28rem] w-[22rem] overflow-auto rounded-pop border border-ds-line bg-ds-card p-s4 shadow-pop sm:left-auto sm:right-0">
          <div className="mb-3 flex items-center justify-between">
            <h3 className={UI_SECTION}>{t('reports.more_filters')}</h3>
            <button
              type="button"
              onClick={onClear}
              className={`${UI_MUTED} hover:text-ds-text`}
            >
              {t('reports.clear_all')}
            </button>
          </div>

          {locationOptions.length > 0 && (
            <div className="mb-3">
              <MultiSelectFilter
                label={t('reports.filters.location')}
                allLabel={t('reports.filters.all_locations')}
                options={locationOptions}
                selectedIds={filters.locationIds}
                onChange={(ids) => onChange({ ...filters, locationIds: ids })}
              />
            </div>
          )}

          <label className={`mb-3 block ${TYPE.label} text-ds-muted`}>
            {t('reports.filters.appointment_status')}
            <select
              value={filters.appointmentStatus}
              onChange={(event) =>
                onChange({
                  ...filters,
                  appointmentStatus: event.target.value as ReportFilterState['appointmentStatus'],
                })
              }
              className={cn(fieldClass, 'mt-1')}
            >
              <option value="completed">{t('reports.filters.status_completed')}</option>
              <option value="all">{t('reports.filters.status_all')}</option>
              <option value="upcoming">{t('reports.filters.status_upcoming')}</option>
              <option value="cancelled">{t('reports.filters.status_cancelled')}</option>
              <option value="no_show">{t('reports.filters.status_no_show')}</option>
            </select>
          </label>

          <label className={`mb-3 block ${TYPE.label} text-ds-muted`}>
            {t('reports.filters.revenue_basis')}
            <select
              value={filters.revenueBasis}
              onChange={(event) =>
                onChange({
                  ...filters,
                  revenueBasis: event.target.value as ReportFilterState['revenueBasis'],
                })
              }
              className={cn(fieldClass, 'mt-1')}
            >
              <option value="invoiced">{t('reports.filters.basis_invoiced')}</option>
              <option value="paid">{t('reports.filters.basis_paid')}</option>
            </select>
          </label>

          <label className={`mb-3 block ${TYPE.label} text-ds-muted`}>
            {t('reports.filters.group_by')}
            <select
              value={filters.grouping}
              onChange={(event) =>
                onChange({
                  ...filters,
                  grouping: event.target.value as ReportFilterState['grouping'],
                })
              }
              className={cn(fieldClass, 'mt-1')}
            >
              <option value="practitioner_service">
                {t('reports.grouping.practitioner_service')}
              </option>
              <option value="service_practitioner">
                {t('reports.grouping.service_practitioner')}
              </option>
              <option value="practitioner">{t('reports.grouping.practitioner')}</option>
              <option value="service">{t('reports.grouping.service')}</option>
            </select>
          </label>

          <label className={`mb-3 block ${TYPE.label} text-ds-muted`}>
            {t('reports.filters.patient')}
            <input
              type="search"
              value={patientQuery}
              onChange={(event) => setPatientQuery(event.target.value)}
              className={cn(fieldClass, 'mt-1')}
              placeholder={t('reports.filters.patient_search')}
            />
          </label>
          {picker.data && picker.data.length > 0 && (
            <div className="mb-3 max-h-28 overflow-auto rounded-lg border border-ds-line">
              {picker.data.map((patient) => (
                <button
                  key={patient.id}
                  type="button"
                  className={`block w-full px-2 py-1.5 text-left ${TYPE.body} hover:bg-ds-row-hover`}
                  onClick={() => {
                    if (filters.patientIds.includes(patient.id)) return;
                    onChange({ ...filters, patientIds: [...filters.patientIds, patient.id] });
                    setPatientQuery('');
                  }}
                >
                  {patient.first_name} {patient.last_name}
                </button>
              ))}
            </div>
          )}
          {filters.patientIds.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-1">
              {filters.patientIds.map((id) => (
                <button
                  key={id}
                  type="button"
                  className={`rounded-full bg-ds-teal-tint px-s2 py-0.5 ${TYPE.body} text-ds-teal`}
                  onClick={() =>
                    onChange({
                      ...filters,
                      patientIds: filters.patientIds.filter((item) => item !== id),
                    })
                  }
                >
                  {patientLabels[id] ?? id} ×
                </button>
              ))}
            </div>
          )}

          <label className={`mb-3 block ${TYPE.label} text-ds-muted`}>
            {t('reports.filters.billing_status')}
            <select
              multiple
              value={filters.invoiceStatuses}
              onChange={(event) =>
                onChange({
                  ...filters,
                  invoiceStatuses: [...event.target.selectedOptions].map((option) => option.value),
                })
              }
              className={cn(fieldClass, 'mt-1 h-24 py-1')}
            >
              {INVOICE_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {t(`invoices.status_${status}`)}
                </option>
              ))}
            </select>
          </label>

          {paymentMethods.length > 0 && (
            <label className={`mb-3 block ${TYPE.label} text-ds-muted`}>
              {t('reports.filters.payment_method')}
              <select
                multiple
                value={filters.paymentMethods}
                onChange={(event) =>
                  onChange({
                    ...filters,
                    paymentMethods: [...event.target.selectedOptions].map((option) => option.value),
                  })
                }
                className={cn(fieldClass, 'mt-1 h-20 py-1')}
              >
                {paymentMethods.map((method) => (
                  <option key={method} value={method}>
                    {method}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className={`mb-3 block ${TYPE.label} text-ds-muted`}>
            {t('reports.filters.new_vs_returning')}
            <select
              value={filters.newVsReturning}
              onChange={(event) =>
                onChange({
                  ...filters,
                  newVsReturning: event.target.value as ReportFilterState['newVsReturning'],
                })
              }
              className={cn(fieldClass, 'mt-1')}
            >
              <option value="all">{t('reports.filters.new_vs_returning_all')}</option>
              <option value="new">{t('reports.filters.new_only')}</option>
              <option value="returning">{t('reports.filters.returning_only')}</option>
            </select>
          </label>

          <div className="mb-3 grid grid-cols-2 gap-2">
            <label className={`${TYPE.label} text-ds-muted`}>
              {t('reports.filters.min_revenue')}
              <input
                type="number"
                min={0}
                step="0.01"
                value={
                  filters.minRevenueCents != null ? (filters.minRevenueCents / 100).toString() : ''
                }
                onChange={(event) =>
                  onChange({
                    ...filters,
                    minRevenueCents: event.target.value
                      ? Math.round(Number(event.target.value) * 100)
                      : null,
                  })
                }
                className={cn(fieldClass, 'mt-1')}
              />
            </label>
            <label className={`${TYPE.label} text-ds-muted`}>
              {t('reports.filters.max_revenue')}
              <input
                type="number"
                min={0}
                step="0.01"
                value={
                  filters.maxRevenueCents != null ? (filters.maxRevenueCents / 100).toString() : ''
                }
                onChange={(event) =>
                  onChange({
                    ...filters,
                    maxRevenueCents: event.target.value
                      ? Math.round(Number(event.target.value) * 100)
                      : null,
                  })
                }
                className={cn(fieldClass, 'mt-1')}
              />
            </label>
          </div>

          <label className={`mb-2 flex items-center gap-2 ${TYPE.body} text-ds-text`}>
            <input
              type="checkbox"
              checked={filters.includeCancelled}
              onChange={(event) => onChange({ ...filters, includeCancelled: event.target.checked })}
              className="h-4 w-4 rounded border-ds-field text-ds-teal focus:ring-ds-teal"
            />
            {t('reports.filters.include_cancelled')}
          </label>
          <label className={`mb-4 flex items-center gap-2 ${TYPE.body} text-ds-text`}>
            <input
              type="checkbox"
              checked={filters.includeNoShows}
              onChange={(event) => onChange({ ...filters, includeNoShows: event.target.checked })}
              className="h-4 w-4 rounded border-ds-field text-ds-teal focus:ring-ds-teal"
            />
            {t('reports.filters.include_no_shows')}
          </label>

          <Button size="sm" className="w-full" onClick={() => setOpen(false)}>
            {t('reports.apply_filters')}
          </Button>
        </div>
      )}
    </div>
  );
}
